import React, { useMemo } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import KpiModuleCard from '../charts/KpiModuleCard';
import TrendKpiCard from '../charts/TrendKpiCard';
import MiniDonutChart from '../charts/MiniDonutChart';
import MiniBarChart from '../charts/MiniBarChart';
import {
  calcGHGEmissions,
  calcWasteDiversionRate,
  calcResourceIntensity,
  calcWasteByPeriod,
} from '../kpiService';
import type { KpiDashboardConfig } from '../kpiConfig';

interface Props {
  energy:          any[];
  water:           any[];
  waste:           any[];
  selectedPackage: string[] | null;
  config:          KpiDashboardConfig;
  stretch?:        boolean;
}

export default function SustainabilityModule({ energy, water, waste, selectedPackage, config, stretch }: Props) {
  const { width } = useWindowDimensions();
  const isNarrow = width < 480;
  const ghg       = useMemo(() => calcGHGEmissions(energy, waste, config), [energy, waste]);
  const diversion = useMemo(() => calcWasteDiversionRate(waste), [waste]);
  const intensity = useMemo(() => calcResourceIntensity(water, energy, selectedPackage, config), [water, energy, selectedPackage]);
  const wastePeriodBars = useMemo(() => calcWasteByPeriod(waste), [waste]);

  const waste_ = Array.isArray(waste) ? waste : [];
  const isDiv = (i: any) => {
    if (i.Diverted === 'Diverted') return true;
    if (i.Diverted === 'Not Diverted') return false;
    const m = (i.WasteDisposalMethod || '').trim().toLowerCase();
    if (!m) return null;
    if (/reuse|recycl|recover|compost|energy\s*recover/i.test(m)) return true;
    if (/landfill|dump|incinerat|other/i.test(m)) return false;
    return null;
  };
  const divertedTonnes    = waste_.filter(i => isDiv(i) === true).reduce((s: number, i: any) => s + (parseFloat(i.QuantityTonne) || 0), 0);
  const notDivertedTonnes = waste_.filter(i => isDiv(i) === false).reduce((s: number, i: any) => s + (parseFloat(i.QuantityTonne) || 0), 0);

  const divertedBars = wastePeriodBars.map(p => ({
    label: p.label,
    value: Math.round(p.diverted),
    color: '#2E7D32',
  }));

  return (
    <KpiModuleCard title="Sustainability & Efficiency KPIs" accentColor="#2E7D32" stretch={stretch}>
      <View style={styles.row}>
        {/* GHG */}
        <TrendKpiCard
          label="GHG Emissions (Est. CO2e)"
          value={ghg.toLocaleString()}
          unit="Tonnes"
          accent="#D32F2F"
          subtitle="From fuel, electricity & landfill"
        />

        {/* Waste Diversion */}
        <View style={styles.donutBlock}>
          <Text style={styles.blockLabel}>WASTE DIVERSION RATE</Text>
          <MiniDonutChart
            segments={[
              { label: 'Diverted',     value: divertedTonnes,    color: '#2E7D32' },
              { label: 'Not Diverted', value: notDivertedTonnes, color: '#BDBDBD' },
            ]}
            size={110}
            centerValue={`${diversion}%`}
            centerLabel="Diverted"
          />
          {divertedBars.length > 0 && (
            <MiniBarChart data={divertedBars} barColor="#2E7D32" height={45} />
          )}
        </View>

        {/* Resource Intensity */}
        <View style={styles.intensityBlock}>
          <Text style={styles.blockLabel}>RESOURCE INTENSITY</Text>
          <View style={styles.intensityRow}>
            <View style={styles.intensityItem}>
              <Text style={[styles.intensityValue, isNarrow && styles.intensityValueNarrow]}>{intensity.waterPerManHour}</Text>
              <Text style={styles.intensityUnit}>L / Man-Hour</Text>
              <Text style={styles.intensityLbl}>Water Intensity</Text>
            </View>
            <View style={styles.intensitySep} />
            <View style={styles.intensityItem}>
              <Text style={[styles.intensityValue, isNarrow && styles.intensityValueNarrow]}>{intensity.kwhPerSqm}</Text>
              <Text style={styles.intensityUnit}>kWh / m²</Text>
              <Text style={styles.intensityLbl}>Energy Intensity</Text>
            </View>
          </View>
        </View>
      </View>
    </KpiModuleCard>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  donutBlock: {
    flex: 1,
    minWidth: 160,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginVertical: 4,
  },
  blockLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
    textAlign: 'center',
  },
  intensityBlock: {
    flex: 1,
    minWidth: 160,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginVertical: 4,
  },
  intensityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginTop: 8,
  },
  intensityItem: {
    flex: 1,
    alignItems: 'center',
  },
  intensitySep: {
    width: 1,
    height: 50,
    backgroundColor: '#E0E0E0',
  },
  intensityValue: {
    fontSize: 26,
    fontWeight: '800',
    color: '#111',
  },
  intensityValueNarrow: {
    fontSize: 20,
  },
  intensityUnit: {
    fontSize: 10,
    color: '#777',
    fontWeight: '600',
    marginTop: 2,
  },
  intensityLbl: {
    fontSize: 9,
    color: '#AAA',
    marginTop: 4,
    textAlign: 'center',
  },
});
